import { doGet2, doPost2WithFormData, doPutWithFormData } from './common'
import { adminRequest } from '../utils/adminRequest'

/**
 * Drafts, publishing and revisions for Puck pages and steps (BR-5691).
 *
 * The live row stays what readers get; the editor writes a draft and publishes it.
 * Every call goes through the admin token (`adminRequest`).
 */

export type BuilderEntityType = 'PAGE' | 'STEP'

/** Page settings carried by a page draft (null for steps). */
export type DraftPageFields = {
  title?: string | null
  slug?: string | null
  url?: string | null
  published?: boolean | null
  lang?: string | null
  translationKey?: string | null
}

export type DraftResponse = {
  id: number
  entityType: BuilderEntityType
  entityId: number
  /** The editor's content string, verbatim. */
  content: string | null
  metadata: string | null
  pageFields: DraftPageFields | null
  created: string | null
  createdBy: number | null
  updated: string | null
  updatedBy: number | null
  baseLiveUpdated: string | null
  liveUpdated: string | null
  /** The draft would change what readers see. */
  differsFromLive: boolean
  /** Someone wrote live after this draft started (Beaker2 or another publish). */
  liveChangedSinceDraft: boolean
  /** Bumped by every save of the draft; sent back as `draftVersion` (Addendum 1 A). */
  version?: number
}

export type RevisionSource = 'PUBLISH' | 'DIRECT_UPDATE' | 'RESTORE'

export type BuilderRevisionSummary = {
  id: number
  entityType: BuilderEntityType
  entityId: number
  revisionNumber: number
  /** When this version went live. */
  liveFrom: string | null
  /** Who made it live. */
  liveBy: number | null
  /** When it was replaced. */
  created: string | null
  /** Who replaced it. */
  createdBy: number | null
  /** Emails of liveBy and createdBy, when the API knows the user (BR-5691 QA). */
  liveByEmail?: string | null
  createdByEmail?: string | null
  source: RevisionSource
  restoredFromRevisionId: number | null
}

/** One revision with its content (`GET /admin/builder-revision/{id}`). */
export type BuilderRevision = BuilderRevisionSummary & {
  /** The content string as it was live, verbatim. */
  content: string | null
  metadata: string | null
  /** The page settings as they were live (null for steps). */
  pageFields: DraftPageFields | null
}

/** A non-OK API answer, with the API's own message when it sent one. */
export class ApiError extends Error {
  status: number
  /** The body's `error` field: a code such as `DRAFT_CONFLICT`, or the reason phrase. */
  code?: string
  /** The parsed JSON body, when there was one. */
  body?: Record<string, unknown>

  constructor(
    status: number,
    message: string,
    code?: string,
    body?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.body = body
  }
}

/**
 * Checked by shape rather than `instanceof`: a transpiled subclass of Error does not
 * always keep its prototype.
 */
export const isApiError = (error: unknown): error is ApiError =>
  !!error &&
  typeof error === 'object' &&
  typeof (error as ApiError).status === 'number' &&
  typeof (error as ApiError).message === 'string'

export const LIVE_CHANGED_PROMPT =
  'The live version changed since this draft started. Publish anyway?'

/** `draftVersion` meaning "the editor believes there is no draft". */
export const NO_DRAFT_VERSION = -1

/** 409 code: the draft was saved by someone else since the editor loaded it. */
export const DRAFT_CONFLICT = 'DRAFT_CONFLICT'

/** 409 code on publish: live was written since the draft started. */
export const LIVE_CHANGED = 'LIVE_CHANGED'

/**
 * The `draftVersion` to send with a save or publish: the loaded draft's version, or
 * NO_DRAFT_VERSION when there is no draft. Undefined (no check) when the API sent a
 * draft without a version, so an older API never sees a version it did not issue.
 */
export const draftVersionOf = (
  draft: DraftResponse | null | undefined,
): number | undefined => {
  if (!draft) return NO_DRAFT_VERSION
  return typeof draft.version === 'number' ? draft.version : undefined
}

/** What a DRAFT_CONFLICT 409 says about the draft that is on the server now. */
export type DraftConflict = {
  /** The API's own sentence, e.g. "User #12 saved this draft at <ISO>." */
  message: string
  /** The server draft's version, null when the server has no draft. */
  draftVersion: number | null
  draftUpdated: string | null
  draftUpdatedBy: number | null
  /** The saver's email, when the API knows the user. */
  draftUpdatedByEmail: string | null
}

const numberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const stringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null

/** The conflict details when `error` is a DRAFT_CONFLICT 409, else null. */
export const readDraftConflict = (error: unknown): DraftConflict | null => {
  if (!isApiError(error) || error.status !== 409) return null
  if (error.code !== DRAFT_CONFLICT) return null
  const body = error.body ?? {}
  return {
    message: error.message,
    draftVersion: numberOrNull(body.draftVersion),
    draftUpdated: stringOrNull(body.draftUpdated),
    draftUpdatedBy: numberOrNull(body.draftUpdatedBy),
    draftUpdatedByEmail: stringOrNull(body.draftUpdatedByEmail),
  }
}

/**
 * The draft version a failed publish reports, or null when it does not say.
 *
 * A publish that carries content saves it as the draft first, in its own transaction,
 * and only then publishes. When the publish part fails (a LIVE_CHANGED 409, a 400 from
 * the page validators or the slug check) that save stays, so the server draft is one
 * version ahead of what the editor last saw. The API puts the version it now has in the
 * error body (-1 when there is no draft). A DRAFT_CONFLICT carries one too, but there
 * nothing was saved: it is the other person's version.
 */
export const readReportedDraftVersion = (error: unknown): number | null =>
  isApiError(error) ? numberOrNull(error.body?.draftVersion) : null

/**
 * A publish 409 that is about live, not the draft. Before the API sent codes every
 * publish 409 meant this (its body then carried the reason phrase "Conflict"), so any
 * 409 that is not a DRAFT_CONFLICT counts.
 */
export const isLiveChangedError = (error: unknown): boolean =>
  isApiError(error) && error.status === 409 && error.code !== DRAFT_CONFLICT

const BASE_PATHS: Record<BuilderEntityType, string> = {
  PAGE: '/admin/page-builder',
  STEP: '/admin/step-builder',
}

export const REVISIONS_PATH = '/admin/builder-revision'

/** Form fields as the multipart endpoints take them. */
export type FormPayload = Record<string, string | number | boolean>

/**
 * Reads the body once and turns a failed response into an ApiError carrying the API's
 * `message` (or `error`), so the editor can show what actually went wrong.
 */
export const toApiError = async (
  response: Response,
  fallback = 'Request failed',
): Promise<ApiError> => {
  const text = await response.text().catch(() => '')
  let body: Record<string, unknown> | undefined
  try {
    const parsed = text ? JSON.parse(text) : null
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      body = parsed as Record<string, unknown>
    }
  } catch {
    // Not JSON (an HTML error page, a proxy). The status says more than the body.
  }
  const message = stringOrNull(body?.message) ?? stringOrNull(body?.error)
  return new ApiError(
    response.status,
    message ?? `${fallback} (${response.status})`,
    stringOrNull(body?.error) ?? undefined,
    body,
  )
}

const readJson = async <T>(
  response: Response,
  fallback: string,
): Promise<T> => {
  if (!response.ok) throw await toApiError(response, fallback)
  const text = await response.text()
  if (!text) throw new ApiError(response.status, `${fallback}: empty response`)
  return JSON.parse(text) as T
}

let queue: Promise<unknown> = Promise.resolve()

/**
 * Runs the editor's API calls one after another.
 *
 * `adminRequest` swaps the token and site on the shared API client for the length of
 * the call and restores them afterwards. Anything sent meanwhile goes out with the admin
 * token and `x-btm-site: dailybreak.com`: two overlapping admin calls restore each
 * other's values (which can leave the admin token behind as the reader token), and a
 * public call such as the page list would be scoped to the wrong site. Queuing every
 * editor call keeps them from overlapping.
 */
export const runQueued = <T>(fn: () => Promise<T>): Promise<T> => {
  const run = queue.then(fn)
  queue = run.catch(() => undefined)
  return run
}

/** `adminRequest`, queued (see runQueued). */
export const runAdmin = <T>(fn: () => Promise<T>): Promise<T> =>
  runQueued(() => adminRequest(fn))

/** The admin entity (`Page` / `StepBuilderStep`), i.e. the live version. */
export const fetchBuilderEntity = <T>(
  type: BuilderEntityType,
  id: number | string,
): Promise<T> =>
  runAdmin(async () =>
    readJson<T>(
      await doGet2(`${BASE_PATHS[type]}/${id}`, {}),
      'Failed to load',
    ),
  )

/** The entity's draft, or null when it has none (404). */
export const fetchDraft = (
  type: BuilderEntityType,
  id: number | string,
): Promise<DraftResponse | null> =>
  runAdmin(async () => {
    const response = await doGet2(`${BASE_PATHS[type]}/${id}/draft`, {})
    if (response.status === 404) return null
    return readJson<DraftResponse>(response, 'Failed to load the draft')
  })

/**
 * How a draft write is checked against the server's draft (Addendum 1 A).
 *
 * `draftVersion` is the version the editor loaded (NO_DRAFT_VERSION for none); the API
 * answers a DRAFT_CONFLICT 409 when its draft is not that version. Left out, nothing is
 * checked. `force` skips the checks.
 *
 * `baseLiveUpdated` is the live `updated` the editor loaded, exactly as the admin GET
 * returned it. It only means something when there is no draft yet: the API then starts
 * the draft from it, so a live change made after the editor loaded is still a
 * LIVE_CHANGED on publish. Without it the API would start the draft from live as it is
 * at that moment, and publish would overwrite the change without asking. Once a draft
 * exists its own base is used, so the field is sent only with NO_DRAFT_VERSION.
 */
export type DraftWriteOptions = {
  draftVersion?: number
  force?: boolean
  baseLiveUpdated?: string
}

/** The checking fields, added only when set: the form has no way to send "undefined". */
export const draftWriteFields = ({
  draftVersion,
  force,
  baseLiveUpdated,
}: DraftWriteOptions = {}): FormPayload => ({
  ...(typeof draftVersion === 'number' ? { draftVersion } : {}),
  ...(force ? { force: true } : {}),
  ...(draftVersion === NO_DRAFT_VERSION && baseLiveUpdated
    ? { baseLiveUpdated }
    : {}),
})

/** Creates or replaces the draft. Live is untouched. */
export const saveDraft = (
  type: BuilderEntityType,
  id: number | string,
  payload: FormPayload,
  options?: DraftWriteOptions,
): Promise<DraftResponse> =>
  runAdmin(async () =>
    readJson<DraftResponse>(
      await doPutWithFormData(`${BASE_PATHS[type]}/${id}/draft`, {
        ...payload,
        ...draftWriteFields(options),
      }),
      'Failed to save the draft',
    ),
  )

/**
 * Publishes. With content in the payload the API saves it as the draft first, so
 * unsaved edits go live too. Answers 409 LIVE_CHANGED when live changed since the
 * draft started and 409 DRAFT_CONFLICT when the draft is not `draftVersion`, unless
 * `force` is set.
 */
export const publishEntity = <T>(
  type: BuilderEntityType,
  id: number | string,
  payload: FormPayload,
  options?: DraftWriteOptions,
): Promise<T> =>
  runAdmin(async () =>
    readJson<T>(
      await doPost2WithFormData(`${BASE_PATHS[type]}/${id}/publish`, {
        ...payload,
        ...draftWriteFields(options),
      }),
      'Failed to publish',
    ),
  )

/** Creates the entity (written live: nothing references a new step or page yet). */
export const createEntity = <T>(
  type: BuilderEntityType,
  payload: FormPayload,
): Promise<T> =>
  runAdmin(async () =>
    readJson<T>(
      await doPost2WithFormData(BASE_PATHS[type], payload),
      'Failed to create',
    ),
  )

/** Writes live directly (the legacy save, still what Beaker2 uses). */
export const updateEntityLive = <T>(
  type: BuilderEntityType,
  id: number | string,
  payload: FormPayload,
): Promise<T> =>
  runAdmin(async () =>
    readJson<T>(
      await doPutWithFormData(`${BASE_PATHS[type]}/${id}`, payload),
      'Failed to save',
    ),
  )

/** The latest revisions, newest first (no content). */
export const listRevisions = (
  type: BuilderEntityType,
  id: number | string,
  size = 20,
): Promise<BuilderRevisionSummary[]> =>
  runAdmin(async () => {
    const page = await readJson<{ content?: BuilderRevisionSummary[] }>(
      await doGet2(REVISIONS_PATH, {
        entityType: type,
        entityId: id,
        size,
      }),
      'Failed to load the history',
    )
    return page.content ?? []
  })

/** One revision, with the content it had while it was live. */
export const fetchRevision = (revisionId: number): Promise<BuilderRevision> =>
  runAdmin(async () =>
    readJson<BuilderRevision>(
      await doGet2(`${REVISIONS_PATH}/${revisionId}`, {}),
      'Failed to load this version',
    ),
  )

/** Copies a revision into the entity's draft. Live changes only on Publish. */
export const restoreRevision = (revisionId: number): Promise<DraftResponse> =>
  runAdmin(async () =>
    readJson<DraftResponse>(
      await doPost2WithFormData(`${REVISIONS_PATH}/${revisionId}/restore`, {}),
      'Failed to restore',
    ),
  )

/**
 * Publish, and when the API reports that live moved on since the draft started (409
 * LIVE_CHANGED), ask before overwriting it. `confirm` may answer later (the editor's
 * dialog). Resolves null when the author declines, after handing the declined error to
 * `onDeclined` (its body says what the server draft is now). A DRAFT_CONFLICT is
 * rethrown: it has its own dialog, not this prompt.
 */
export const publishWithConfirm = async <T>(
  publish: (force: boolean) => Promise<T>,
  confirm: (message: string) => boolean | Promise<boolean>,
  onDeclined?: (error: unknown) => void,
): Promise<T | null> => {
  try {
    return await publish(false)
  } catch (error) {
    if (!isLiveChangedError(error)) throw error
    if (!(await confirm(LIVE_CHANGED_PROMPT))) {
      onDeclined?.(error)
      return null
    }
    return publish(true)
  }
}
